import React from 'react'
import axios from 'axios'
import moment from 'moment'
import {
  CalendarPlus,
  CircleAlert,
  CircleX,
  Clock,
  Copy,
  LoaderCircle,
  LogIn,
  Mail,
  MessageSquare,
  MessageSquareMore,
  Pencil,
  SquarePen,
  Star,
  Video,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import Modal from '../../../modal'
import DatetimePicker from '../../../datetime_picker'
import CopyToClipboard from '../../../copy_to_clipboard'

const STATE_STYLES = {
  scheduled: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-muted text-muted-foreground',
  missed: 'bg-red-100 text-red-700',
  happened: 'bg-emerald-100 text-emerald-800',
  awaiting_response: 'bg-amber-100 text-amber-800',
  default: 'bg-muted text-foreground'
}

const ICON_COLOR = {
  success: 'text-emerald-500',
  info: 'text-blue-500',
  warning: 'text-amber-500',
  danger: 'text-red-500',
  secondary: 'text-muted-foreground'
}

const MISSED_REASON_LABEL = {
  forgot: 'they forgot',
  loadshedding: 'loadshedding',
  other: 'other'
}

const NOTIFICATION_TYPE_LABEL = {
  scheduled: 'Confirmation',
  rescheduled: 'Reschedule',
  cancelled: 'Cancellation',
  alternative_suggested: 'Alternative time',
  reminder_24h: '24-hour reminder',
  reminder_5m: '5-minute reminder',
  follow_up: 'Follow-up'
}

function displayState(meeting) {
  if (!meeting) return 'default'
  if (meeting.workflow_state === 'cancelled') return 'cancelled'
  if ((meeting.outcomes || []).some((o) => o.status === 'didnt_happen')) return 'missed'
  if ((meeting.outcomes || []).some((o) => o.status === 'happened')) return 'happened'
  if (meeting.missed) return 'missed'
  if (moment(meeting.end_time).isBefore(moment())) return 'awaiting_response'
  return 'scheduled'
}

const CARD_CLASS = 'rounded-[10px] border border-border bg-card p-4'
const HEADING_CLASS = 'mb-3 border-b pb-3 text-sm font-semibold'

class MeetingModal extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      meeting: null,
      loading: true,
      cancelling: false,
      confirmCancel: false,
      editing: false,
      newStartTime: null,
      saving: false,
      recordings: null,
      recordingsLoading: false,
      fetchingPlayback: {},
      participantFilter: 'all'
    }
  }

  componentDidMount() {
    this.fetchMeeting()
  }

  fetchMeeting = () => {
    const { tutoringJobId, meetingId } = this.props
    axios.get(`/api/sales/tutoring_jobs/${tutoringJobId}/meetings/${meetingId}`)
      .then((response) => this.setState({ meeting: response.data, loading: false }))
      .catch((error) => {
        window.handleError(error)
        this.setState({ loading: false })
      })
  }

  fetchRecordings = () => {
    const { tutoringJobId, meetingId } = this.props
    this.setState({ recordingsLoading: true })
    axios.get(`/api/sales/tutoring_jobs/${tutoringJobId}/meetings/${meetingId}/recordings`)
      .then((response) => this.setState({ recordings: response.data.recordings || [], recordingsLoading: false }))
      .catch((error) => {
        window.handleError(error)
        this.setState({ recordingsLoading: false })
      })
  }

  openPlayback = (sessionId) => {
    this.setState((s) => ({ fetchingPlayback: { ...s.fetchingPlayback, [sessionId]: true } }))
    axios.get(`/api/sales/lesson_space/sessions/${sessionId}/playback_url`)
      .then((response) => {
        this.setState((s) => ({ fetchingPlayback: { ...s.fetchingPlayback, [sessionId]: false } }))
        if (response.data.playback_url) window.open(response.data.playback_url, '_blank')
      })
      .catch((error) => {
        window.handleError(error)
        this.setState((s) => ({ fetchingPlayback: { ...s.fetchingPlayback, [sessionId]: false } }))
      })
  }

  onCancel = () => {
    const { tutoringJobId, meetingId } = this.props
    this.setState({ cancelling: true })

    axios.delete(`/api/sales/tutoring_jobs/${tutoringJobId}/meetings/${meetingId}`)
      .then(() => {
        window.showMessage('Meeting cancelled')
        if (this.props.onMeetingChanged) this.props.onMeetingChanged()
        this.props.onClose()
      })
      .catch((error) => {
        window.handleError(error)
        this.setState({ cancelling: false })
      })
  }

  onSaveTime = () => {
    const { meeting, newStartTime } = this.state
    if (!newStartTime) return

    const { tutoringJobId, meetingId } = this.props
    const duration = moment(meeting.end_time).diff(moment(meeting.start_time), 'minutes')
    const end_time = moment(newStartTime).add(duration, 'minutes').toISOString()

    this.setState({ saving: true })

    axios.put(`/api/sales/tutoring_jobs/${tutoringJobId}/meetings/${meetingId}`, {
      start_time: newStartTime,
      end_time
    })
      .then((response) => {
        window.showMessage('Meeting time updated')
        this.setState({ meeting: response.data, editing: false, newStartTime: null, saving: false })
        if (this.props.onMeetingChanged) this.props.onMeetingChanged()
      })
      .catch((error) => {
        window.handleError(error)
        this.setState({ saving: false })
      })
  }

  participantProfilePath(p) {
    if (p.type === 'Tutor') return `/tutors/${p.participant_id}/activity`
    if (p.type === 'Customer') return `/customers/${p.participant_id}/account`
    if (p.type === 'StaffMember') return `/staff-members/${p.participant_id}/edit`
    return '#'
  }

  renderStateBadge(meeting) {
    const state = displayState(meeting)
    const style = STATE_STYLES[state] || STATE_STYLES.default
    return (
      <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium ${style}`}>
        {state.replace(/_/g, ' ')}
      </span>
    )
  }

  renderHeader(meeting) {
    if (this.state.editing) {
      return (
        <div>
          <DatetimePicker
            value={this.state.newStartTime || meeting.start_time}
            onChange={(v) => this.setState({ newStartTime: v })}
            show={true}
          />
          <p className="my-2 text-xs text-muted-foreground">All participants will be notified of the new time.</p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => this.setState({ editing: false, newStartTime: null })}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={this.onSaveTime}
              disabled={this.state.saving}
            >
              {this.state.saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )
    }

    return (
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold">{moment(meeting.start_time).format('ddd D MMM YYYY')}</div>
          <div className="text-sm text-muted-foreground">
            {moment(meeting.start_time).format('h:mm A')} – {moment(meeting.end_time).format('h:mm A')}
            {meeting.workflow_state !== 'cancelled' && (meeting.outcomes || []).length === 0 && moment(meeting.end_time).isAfter(moment()) && (
              <button
                type="button"
                className="ml-2 cursor-pointer border-0 bg-transparent p-0 text-primary hover:text-primary/80"
                onClick={() => this.setState({ editing: true, newStartTime: meeting.start_time })}
                title="Edit time"
              >
                <Pencil className="size-3.5"/>
              </button>
            )}
          </div>
          {meeting.job_reference && (
            <div className="mt-1 text-xs text-muted-foreground">Job ref: {meeting.job_reference}</div>
          )}
        </div>
        {this.renderStateBadge(meeting)}
      </div>
    )
  }

  renderCancelControls(meeting) {
    if (this.state.editing || meeting.workflow_state === 'cancelled') return null
    if (moment(meeting.end_time).isBefore(moment())) return null

    if (this.state.confirmCancel) {
      return (
        <div className="mt-4 rounded-[10px] bg-muted p-3">
          <p className="mb-2 text-xs text-destructive">Cancel this meeting? All participants will be notified.</p>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => this.setState({ confirmCancel: false })}
              disabled={this.state.cancelling}
            >
              Keep
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={this.onCancel}
              disabled={this.state.cancelling}
            >
              {this.state.cancelling ? 'Cancelling...' : 'Cancel meeting'}
            </Button>
          </div>
        </div>
      )
    }

    return (
      <div className="mt-3">
        <Button
          variant="destructive"
          size="sm"
          onClick={() => this.setState({ confirmCancel: true })}
        >
          Cancel meeting
        </Button>
      </div>
    )
  }

  renderParticipants(meeting) {
    return (
      <div className={CARD_CLASS}>
        <div className={HEADING_CLASS}><strong>Participants</strong></div>
        <div className="space-y-2">
          {(meeting.participants || []).map((p) => (
            <div key={p.id} className="flex items-center gap-3">
              {p.profile_picture_url ? (
                <img src={p.profile_picture_url} alt={p.name} className="size-8 flex-none rounded-full object-cover" />
              ) : (
                <div className="flex size-8 flex-none items-center justify-center rounded-full bg-muted text-xs font-bold">
                  {(p.first_name || p.name || '?')[0]}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs text-muted-foreground">{p.type === 'StaffMember' ? 'Staff' : p.type}</div>
                <div className="flex items-center gap-2">
                  <a href={this.participantProfilePath(p)} target="_blank" rel="noopener noreferrer" className="truncate">{p.name}</a>
                  {p.host && <span title="Host"><Star className="size-3 text-amber-500"/></span>}
                </div>
                {p.note && <div className="truncate text-xs text-muted-foreground">{p.note}</div>}
              </div>
              {p.meeting_url && (
                <div className="flex flex-none items-center gap-2">
                  {meeting.workflow_state === 'scheduled' && (
                    <Button asChild size="icon-xs" title={`Launch as ${p.first_name || p.name}`}>
                      <a href={p.meeting_url} target="_blank" rel="noopener noreferrer">
                        <Video className="size-3.5"/>
                      </a>
                    </Button>
                  )}
                  <CopyToClipboard content={p.meeting_url}>
                    <span className="inline-flex size-6 cursor-pointer items-center justify-center rounded-4xl border border-border text-muted-foreground hover:bg-muted" title="Copy meeting link">
                      <Copy className="size-3.5"/>
                    </span>
                  </CopyToClipboard>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  renderOutcomes(meeting) {
    const outcomes = meeting.outcomes || []
    if (outcomes.length === 0) return null

    const headerFor = (o) => {
      if (o.responder_role === 'tutor') {
        return o.status === 'didnt_happen'
          ? <span className="text-red-700">Customer missed the meeting{o.missed_reason ? ` (${MISSED_REASON_LABEL[o.missed_reason] || o.missed_reason})` : ''}</span>
          : <span>Tutor confirmed the meeting{o.rating ? ` · ${o.rating}★` : ''}</span>
      }
      if (o.status === 'didnt_happen') return <span className="text-red-700">Customer said the meeting didn't happen</span>
      if (o.feedback) return <span className="text-red-700">Customer says this tutor isn't a fit</span>
      return <span className="text-emerald-700">Customer wants to go ahead with this tutor</span>
    }

    return (
      <div className={CARD_CLASS}>
        <div className={HEADING_CLASS}><strong>Outcomes</strong></div>
        {outcomes.map((o) => (
          <div key={o.id} className="border-b py-2 last:border-0">
            <div>{headerFor(o)}</div>
            {o.feedback && (
              <div className="mt-0.5 whitespace-pre-wrap text-sm italic text-muted-foreground">"{o.feedback}"</div>
            )}
            {o.missed_reason_text && (
              <div className="mt-0.5 whitespace-pre-wrap text-sm italic text-muted-foreground">"{o.missed_reason_text}"</div>
            )}
            <div className="mt-1 text-xs text-muted-foreground/70">{moment(o.created_at).format('ddd D MMM YYYY, h:mm A')}</div>
          </div>
        ))}
      </div>
    )
  }

  renderRecordingsAndTranscriptions(meeting) {
    const { recordings, recordingsLoading, fetchingPlayback } = this.state
    const transcriptions = meeting.transcriptions || []
    const hasLaunches = (meeting.launches || []).some((l) => l.client_url)

    if (!hasLaunches && transcriptions.length === 0) return null

    return (
      <div className={CARD_CLASS}>
        <div className={HEADING_CLASS}><strong>Recordings &amp; Transcriptions</strong></div>

        <div className="mb-4">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Recordings</div>
          {recordings === null && !recordingsLoading && hasLaunches && (
            <Button variant="outline" size="sm" onClick={this.fetchRecordings}>
              <Video className="size-3.5"/>Fetch recordings
            </Button>
          )}
          {recordings === null && !recordingsLoading && !hasLaunches && (
            <div className="text-sm text-muted-foreground">No recordings available yet.</div>
          )}
          {recordingsLoading && (
            <div className="text-sm text-muted-foreground"><LoaderCircle className="mr-1 inline size-3.5 animate-spin"/>Loading...</div>
          )}
          {recordings && recordings.length === 0 && (
            <div className="text-sm text-muted-foreground">No recordings available yet.</div>
          )}
          {(recordings || []).map((r) => (
            <div key={r.session_id} className="flex items-center justify-between border-b py-2 last:border-0">
              <div>
                <div>{moment(r.start_time).format('ddd D MMM YYYY, h:mm A')}</div>
                <div className="text-xs text-muted-foreground">
                  <Clock className="mr-1 inline size-3"/>{Math.round((r.duration_seconds || 0) / 60)} min
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => this.openPlayback(r.session_id)}
                disabled={fetchingPlayback[r.session_id]}
              >
                {fetchingPlayback[r.session_id] ? 'Loading...' : 'View'}
              </Button>
            </div>
          ))}
        </div>

        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Transcriptions</div>
          {transcriptions.length === 0 && (
            <div className="text-sm text-muted-foreground">No transcriptions available yet.</div>
          )}
          {[...transcriptions].reverse().map((t) => (
            <div key={t.id} className="flex items-center justify-between border-b py-2 last:border-0">
              <div>
                <div>{moment(t.time).format('ddd D MMM YYYY, h:mm A')}</div>
                <div className="text-xs text-muted-foreground">
                  {t.transcription_fetched ? 'Available' : 'Pending'}
                  {t.summary_present && ' · Summary ready'}
                </div>
              </div>
              <Button asChild variant="outline" size="sm">
                <a
                  href={`/lesson_space/transcriptions/${t.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View
                </a>
              </Button>
            </div>
          ))}
        </div>
      </div>
    )
  }

  buildTimelineEvents(meeting) {
    const events = []

    for (const change of (meeting.changes || [])) {
      if (change.change_type === 'scheduled') {
        events.push({
          time: change.created_at,
          icon: CalendarPlus,
          color: 'success',
          label: 'Meeting scheduled',
          detail: `${moment(change.new_start_time).format('ddd D MMM, h:mm A')} - ${moment(change.new_end_time).format('h:mm A')}`,
          participantId: null
        })
      } else if (change.change_type === 'rescheduled') {
        events.push({
          time: change.created_at,
          icon: Clock,
          color: 'info',
          label: `Rescheduled by ${change.changed_by}`,
          detail: `${moment(change.previous_start_time).format('ddd D MMM, h:mm A')} → ${moment(change.new_start_time).format('ddd D MMM, h:mm A')}`,
          participantId: null
        })
      } else {
        events.push({
          time: change.created_at,
          icon: SquarePen,
          color: 'secondary',
          label: change.change_type,
          detail: `by ${change.changed_by}`,
          participantId: null
        })
      }
    }

    for (const launch of (meeting.launches || [])) {
      events.push({
        time: launch.created_at,
        icon: LogIn,
        color: launch.client_url ? 'success' : 'warning',
        label: `${launch.user_name || launch.user_type} joined`,
        detail: launch.client_url ? 'Successfully connected' : 'Outside launch window',
        participantId: launch.participant_id
      })
    }

    if (meeting.workflow_state === 'cancelled') {
      events.push({
        time: meeting.updated_at,
        icon: CircleX,
        color: 'danger',
        label: `Cancelled by ${meeting.cancelled_by || 'unknown'}`,
        detail: meeting.cancellation_reason || '',
        participantId: null
      })
    }

    const channelIcon = (channel) => channel === 'sms' ? MessageSquare : Mail
    const channelLabel = (channel) => channel === 'sms' ? 'SMS' : 'email'
    const channelsFor = (channel) => channel === 'email_and_sms' ? ['email', 'sms'] : [channel]
    const typeLabel = (t) => NOTIFICATION_TYPE_LABEL[t] || t.replace(/_/g, ' ')

    for (const o of (meeting.outcomes || [])) {
      let label
      if (o.responder_role === 'tutor') {
        label = o.status === 'didnt_happen'
          ? `Customer missed the meeting${o.missed_reason ? ` (${MISSED_REASON_LABEL[o.missed_reason] || o.missed_reason})` : ''}`
          : `Tutor confirmed the meeting${o.rating ? ` · ${o.rating}★` : ''}`
      } else if (o.status === 'didnt_happen') {
        label = "Customer said the meeting didn't happen"
      } else if (o.feedback) {
        label = "Customer says this tutor isn't a fit"
      } else {
        label = 'Customer wants to go ahead with this tutor'
      }
      const detailParts = []
      if (o.feedback) detailParts.push(`"${o.feedback}"`)
      if (o.missed_reason_text) detailParts.push(`"${o.missed_reason_text}"`)
      events.push({
        time: o.created_at,
        icon: o.status === 'didnt_happen' ? CircleAlert : MessageSquareMore,
        color: o.status === 'didnt_happen' ? 'danger' : 'success',
        label,
        detail: detailParts.join(' '),
        participantId: o.participant_id
      })
    }

    for (const n of (meeting.notifications || [])) {
      if (n.cancelled_at) continue

      const channels = channelsFor(n.channel)
      const channelText = channels.map(channelLabel).join(' & ')
      const baseLabel = `${typeLabel(n.notification_type)} ${channelText}`
      const base = { icons: channels.map(channelIcon), participantId: n.participant_id, muted: true }

      if (n.sent_at) {
        events.push({
          ...base,
          time: n.sent_at,
          color: 'success',
          badge: { text: 'SENT', style: 'bg-emerald-100 text-emerald-800' },
          label: `${baseLabel} to ${n.recipient_name}`
        })
      } else if (n.processing_at) {
        events.push({
          ...base,
          time: n.processing_at,
          color: 'warning',
          label: `${baseLabel} sending to ${n.recipient_name}`
        })
      } else {
        events.push({
          ...base,
          time: n.due_at || n.created_at,
          color: 'info',
          badge: { text: 'SCHEDULED', style: 'bg-blue-100 text-blue-800' },
          label: `${baseLabel} for ${n.recipient_name}`,
          detail: n.due_at ? `Due ${moment(n.due_at).format('D MMM, h:mm A')}` : ''
        })
      }
    }

    return events
  }

  renderTimeline(meeting) {
    const events = this.buildTimelineEvents(meeting)
    const { participantFilter } = this.state

    const filtered = participantFilter === 'all'
      ? events
      : events.filter((e) => e.participantId == null || String(e.participantId) === participantFilter)

    filtered.sort((a, b) => new Date(b.time) - new Date(a.time))

    return (
      <div>
        {filtered.length === 0 && (
          <div className="text-sm text-muted-foreground">No events for this participant.</div>
        )}
        {filtered.map((event, i) => (
          <div key={i} className={`mb-4 flex items-start ${event.muted ? 'text-muted-foreground' : ''}`}>
            <div className="mr-4 flex min-w-8 justify-center gap-1 text-center">
              {(event.icons || [event.icon]).map((Icon, j) => (
                <Icon key={j} className={`size-4 ${event.muted ? 'text-muted-foreground' : (ICON_COLOR[event.color] || ICON_COLOR.secondary)}`} />
              ))}
            </div>
            <div>
              <div className="flex items-center gap-2">
                {event.badge && (
                  <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${event.badge.style}`}>
                    {event.badge.text}
                  </span>
                )}
                <strong className={event.muted ? 'font-normal text-muted-foreground' : ''}>{event.label}</strong>
              </div>
              {event.detail && <div className="text-xs text-muted-foreground">{event.detail}</div>}
              <div className={`text-xs ${event.muted ? 'text-muted-foreground' : 'text-muted-foreground/70'}`}>{moment(event.time).format('ddd D MMM YYYY, h:mm A')}</div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  renderBody() {
    const { meeting, loading } = this.state

    if (loading) return <div className="text-center p-12"><LoaderCircle className="inline-block size-4 animate-spin"/></div>
    if (!meeting) return <div className="text-center p-12">Meeting not found</div>

    return (
      <div className="p-4">
        <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-4">
            <div className={CARD_CLASS}>
              {this.renderHeader(meeting)}
              {this.renderCancelControls(meeting)}
            </div>
            {this.renderParticipants(meeting)}
            {this.renderOutcomes(meeting)}
            {this.renderRecordingsAndTranscriptions(meeting)}
          </div>
          <div className={CARD_CLASS}>
            <div className={`${HEADING_CLASS} flex items-center justify-between gap-4`}>
              <strong>Timeline</strong>
              {(meeting.participants || []).length > 0 && (
                <select
                  value={this.state.participantFilter}
                  onChange={(e) => this.setState({ participantFilter: e.target.value })}
                  className="rounded-md border border-border bg-background px-2 py-1 text-xs"
                >
                  <option value="all">All participants</option>
                  {meeting.participants.map((p) => (
                    <option key={p.id} value={String(p.id)}>{p.first_name || p.name}</option>
                  ))}
                </select>
              )}
            </div>
            {this.renderTimeline(meeting)}
          </div>
        </div>
      </div>
    )
  }

  render() {
    return (
      <Modal title="Meeting" size="modal-xl" onClose={this.props.onClose}>
        {this.renderBody()}
      </Modal>
    )
  }
}

export default MeetingModal
